
import { 
  Firestore,
  doc,
  getDoc
} from "firebase/firestore";
import { Offer } from "@/types/offer";
import { prepareOfferData } from "./offerDataPreparation";
import { withSessionFactEdits } from "@/functions-shared/offer/storedSessionPrice";
import { withSessionWindowEdit } from "@/functions-shared/offer/storedSessionWindow";

/**
 * Generate a diff between current offer and new data
 */
export const getDiffForOffer = async (
  db: Firestore,
  offerId: string | null,
  offerData: Partial<Offer>,
  locationId: string,
  userId: string
) => {
  try {
    // For new offers, compare with what would be created
    if (!offerId) {
      // KB-3733 — a create: no stored session price to keep.
      const newOfferData = await prepareOfferData(db, locationId, offerData, userId, null);
      // For a new offer, diff against an empty object
      return { current: {}, updated: newOfferData };
    }
    
    // For existing offers, get the current data
    const offerRef = doc(db, "offers", offerId);
    const offerSnap = await getDoc(offerRef);
    
    if (!offerSnap.exists()) {
      throw new Error(`Offer ${offerId} not found`);
    }
    
    const currentOffer = offerSnap.data() as Offer;
    
    // Get locationId from the reference
    const locationRef = currentOffer.locationRef;
    let offerLocationId = '';
    
    if (typeof locationRef === 'string') {
      // If it's a string path like "campLocations/id"
      const parts = locationRef.split('/');
      offerLocationId = parts[parts.length - 1];
    } else if (locationRef?.id) {
      // If it's a DocumentReference
      offerLocationId = locationRef.id;
    } else {
      throw new Error(`Invalid locationRef in offer ${offerId}`);
    }
    
    // Prepare what would be the updated offer data
    const merged = { ...currentOffer, ...offerData };
    const updatedOfferData = await prepareOfferData(
      db,
      offerLocationId,
      merged,
      userId,
      // KB-3733 — the diff must preview what updateOffer will write, which keeps a camp-data session's price
      // (and, KB-4564, the window the edit sets, as updateOffer does).
      withSessionWindowEdit(
        withSessionFactEdits(currentOffer as unknown as Record<string, unknown>, offerData as Record<string, unknown>),
        offerData as Record<string, unknown>
      )
    );
    
    return { current: currentOffer, updated: updatedOfferData };
  } catch (error) {
    console.error("Error generating offer diff:", error);
    throw error;
  }
};
